import {ChangeDetectorRef, Component, OnInit, ViewContainerRef} from '@angular/core';
import {ConfigurationService} from '../configuration.service';
import {LookupService} from '../../shared/lookup.service';
import {NotificationPopupService} from '../../shared/notification-popup/services/notification-popup.service';

@Component({
    selector: 'app-protected-buckets',
    templateUrl: './protected-buckets.component.html',
    styleUrls: ['./protected-buckets.component.scss']
})
export class ProtectedBucketsComponent implements OnInit {
    // Which protected buckets have their unsold hold subtracted from optimizer capacity.
    // One row per eligible bucket rather than an add/remove list: the eligible set is
    // fixed by the bucket file, so listing it removes typo'd bucket codes as a failure mode.
    public protectedBucketRows: {bucketCode: string; paxClass: string; affectsCapacity: boolean}[] = [];

    // True once the eligible-bucket lookup has succeeded. Until then onSave() leaves the
    // saved value alone instead of posting an empty list, which the batch app would read
    // as "saved, everything off" - a failed lookup must not switch the feature off.
    public protectedBucketsLoaded = false;

    // True when a protected_buckets_capacity row already exists. When it does not, the
    // overnight run is still reading settings.json, so the switches below are not
    // necessarily what is in force and saving would replace it.
    public protectedBucketsConfigSaved = false;

    // Guards the notes below from rendering before the saved config has finished loading.
    public configLoaded = false;

    // True once the bucket lookup has settled, success or failure - distinct from
    // protectedBucketsLoaded, which stays false on failure (see loadProtectedBuckets).
    // Gating the section's spinner on this instead means a failed lookup still reveals the
    // page (with its own "could not load" note) rather than spinning forever.
    private protectedBucketsSettled = false;
    public get loaded(): boolean {
        return this.configLoaded && this.protectedBucketsSettled;
    }

    public saving = false;

    // Saved flags, keyed by bucket code. Held separately because the bucket lookup and
    // the saved configuration are two independent requests and either can land first.
    private savedProtectedFlags: {[bucketCode: string]: boolean} = {};

    constructor(private configurationSvc: ConfigurationService,
                private lookupSvc: LookupService,
                private notificationPopupSvc: NotificationPopupService,
                private viewContainerRef: ViewContainerRef,
                private cdr: ChangeDetectorRef) {
    }

    ngOnInit(): void {
        this.loadProtectedBuckets();
        this.loadSavedConfiguration();
    }

    // True when at least one bucket is switched on, which is when the warning about
    // capacity being removed becomes relevant rather than hypothetical.
    public get anyProtectedBucketEnabled(): boolean {
        return this.protectedBucketRows.some(row => row.affectsCapacity);
    }

    // Prepopulates the screen from the previously saved protected_buckets_capacity set.
    private loadSavedConfiguration(): void {
        this.configurationSvc.getConfiguration().subscribe(config => {
            // affects_capacity is 1/0 rather than a boolean because the same list can be
            // hand written into settings.json and the batch app parses both sources with
            // one validator, so tolerate "1" as well as 1.
            const protectedBuckets = config?.protected_buckets_capacity;
            if (Array.isArray(protectedBuckets)) {
                this.protectedBucketsConfigSaved = true;
                protectedBuckets.forEach(entry => {
                    if (entry?.bucket_code) {
                        this.savedProtectedFlags[entry.bucket_code] = Number(entry.affects_capacity) === 1;
                    }
                });
                this.applySavedProtectedFlags();
            }

            this.configLoaded = true;
            this.cdr.detectChanges();
        }, error => {
            console.error(`Error retrieving saved configuration ${error}`);
            this.configLoaded = true;
            this.cdr.detectChanges();
        });
    }

    // The eligible buckets come from the bucket table, not from the saved list, so a
    // bucket added there shows up switched off and a saved code that is no longer a
    // protected non-flex bucket drops out instead of being written back. The batch app
    // ignores such a code anyway.
    private loadProtectedBuckets(): void {
        this.lookupSvc.loadProtectedBuckets().subscribe((result: any[]) => {
            this.protectedBucketRows = (result || []).map(bucket => ({
                bucketCode: bucket.bucketCode,
                paxClass: bucket.paxClass,
                affectsCapacity: false
            }));
            this.applySavedProtectedFlags();
            this.protectedBucketsLoaded = true;
            this.protectedBucketsSettled = true;
            this.cdr.detectChanges();
        }, error => {
            // Leaves protectedBucketsLoaded false, so a save will not touch the setting.
            console.error(`Error retrieving protected bucket data ${error}`);
            this.protectedBucketsSettled = true;
            this.cdr.detectChanges();
        });
    }

    // Overlays the saved flags onto whatever rows exist. Called from both async
    // callbacks because either can complete first.
    private applySavedProtectedFlags(): void {
        this.protectedBucketRows.forEach(row => {
            row.affectsCapacity = this.savedProtectedFlags[row.bucketCode] === true;
        });
    }

    public onSave(): void {
        // One entry per eligible bucket, on or off - never only the enabled ones. The
        // batch app has to tell "saved, everything off" apart from "never configured",
        // because only the first stops it falling back to settings.json, and a full list
        // is never empty. Omitted entirely when the bucket lookup failed: posting an
        // empty list would read as "everything off" and switch the feature off because a
        // lookup errored. The API upserts per key, so omitting it leaves the row alone.
        if (!this.protectedBucketsLoaded) {
            return;
        }
        const payload = {
            protected_buckets_capacity: this.protectedBucketRows.map(row => ({
                bucket_code: row.bucketCode,
                affects_capacity: row.affectsCapacity ? 1 : 0
            }))
        };

        this.saving = true;
        this.configurationSvc.saveConfiguration(payload).subscribe(() => {
            this.notificationPopupSvc.displayPageNotification(this.viewContainerRef, {
                notificationType: 'success',
                message: 'Configuration saved.'
            });
            this.saving = false;
        }, error => {
            console.error(`Error saving configuration ${error}`);
            this.notificationPopupSvc.displayPageNotification(this.viewContainerRef, {
                notificationType: 'error',
                message: 'Failed to save configuration.'
            });
            this.saving = false;
        });
    }
}
